import * as Yup from 'yup';
import { isValidExperienceFormat } from '../utils/general';
import { DESCRIPTION_CHAR_MIN_LIMIT, Profile_FILE_SIZE, Resume_FILE_SIZE } from './general';

// Login
export const loginValidationSchema = Yup.object().shape({
  email: Yup.string()
    .trim()
    .nullable()
    .email('This email is not a valid format.')
    .required('Email is a required field.'),
  password: Yup.string().trim().required('Password is a required field.')
});

// Register
export const registerValidationSchema = Yup.object().shape({
  firstName: Yup.string()
    .trim()
    .matches(/^[a-zA-Z_ ]+$/, 'Firstname can consist of only alphanumeric characters.')
    .required('Firstname is a required field.')
    .min(3, 'Firstname should have minimum 3 characters.')
    .max(100, 'Firstname can have maximum 100 characters.'),
  lastName: Yup.string()
    .trim()
    .matches(/^[a-zA-Z_ ]+$/, 'Lastname can consist of only alphanumeric characters.')
    .required('Lastname is a required field.')
    .min(3, 'Lastname should have minimum 3 characters.')
    .max(100, 'Lastname can have maximum 100 characters.'),
  email: Yup.string()
    .trim()
    .email('This email is not a valid format.')
    .required('Email is a required field.'),
  password: Yup.string()
    .trim()
    .required('Password is a required field.')
    .min(6, 'Password is too short - should be 6 chars minimum.')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{6,})/,
      'Password has contain minimum 6 characters (includes 1 Upper, 1 Special, 1 Number).'
    ),
  confirmPassword: Yup.string()
    .trim()
    .required('Confirm password is a required field.')
    .oneOf([Yup.ref('password'), null], 'Password does not match.'),
  mobile: Yup.string()
    .trim()
    .matches(/^[0-9]+$/, 'Mobile must be numeric value.')
    .length(10, 'Mobile should have 10 digits.')
    .required('Mobile is a required field.')
});

// Forgot Password
export const forgotPasswordValidationSchema = Yup.object().shape({
  email: Yup.string()
    .trim()
    .nullable()
    .email('This email is not a valid format.')
    .required('Email is a required field.')
});

// Reset Password
export const resetPasswordValidationSchema = Yup.object({
  password: Yup.string()
    .trim()
    .required('Password is a required field.')
    .min(6, 'Password is too short - should be 6 chars minimum.')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{6,})/,
      'Password has contain minimum 6 characters (includes 1 Upper, 1 Special, 1 Number).'
    ),
  confirmPassword: Yup.string()
    .trim()
    .required('Confirm Password is required.')
    .oneOf([Yup.ref('password'), null], 'Password does not match.')
});

// Profile
export const profileValidationSchema = Yup.object().shape({
  firstName: Yup.string()
    .trim()
    .matches(/^[a-zA-Z_ ]+$/, 'Firstname can consist of only alphanumeric characters.')
    .required('Firstname is a required field.')
    .min(3, 'Firstname should have minimum 3 characters.')
    .max(100, 'Firstname can have maximum 100 characters.'),
  middleName: Yup.string()
    .trim()
    .matches(/^[a-zA-Z_ ]+$/, 'Middlename can consist of only alphanumeric characters.')
    .max(100, 'Middlename can have maximum 100 characters.'),
  lastName: Yup.string()
    .trim()
    .matches(/^[a-zA-Z_ ]+$/, 'Lastname can consist of only alphanumeric characters.')
    .required('Lastname is a required field.')
    .min(3, 'Lastname should have minimum 3 characters.')
    .max(100, 'Lastname can have maximum 100 characters.'),
  email: Yup.string()
    .trim()
    .email('This email is not a valid format.')
    .required('Email is a required field.'),
  phone: Yup.string()
    .trim()
    .matches(/^[0-9]+$/, 'Phone must be numeric value.')
    .length(10, 'Phone should have 10 digits.')
    .required('Phone is a required field.'),
  secondPhone: Yup.string()
    .trim()
    .matches(/^[0-9]+$/, 'Second Mobile must be numeric value.')
    .length(10, 'Second Mobile should have 10 digits.')
    .nullable(),
  currentLocation: Yup.string('Current location must be of type string.').required(
    'Current location is required.'
  ),
  location: Yup.string('Current Location must be of type string.')
    .trim()
    .when('currentLocation', {
      is: (value) => value === 'other',
      then: (schema) => schema.required('Current Location is required.')
    }),
  totalExperience: Yup.string()
    .trim()
    .test('valid-format', 'Invalid format. Ensure months do not exceed 11.', (value) =>
      isValidExperienceFormat(value)
    )
    .nullable()
    .required('Experiance is required.'),
  gender: Yup.number().required('Gender is required.'),
  maritalStatus: Yup.number().required('Marital Status is required.'),
  profileImage: Yup.mixed()
    .test('format', 'Only the following formats are accepted: .jpeg, .jpg, .png', (value) => {
      if (!value) return true;
      if (typeof value === 'object') {
        return ['image/jpeg', 'image/jpg', 'image/png'].includes(value.type);
      }
      return true;
    })
    .test('fileSize', 'File size should be less than 1MB', (value) => {
      if (!value) return true;
      if (typeof value === 'object') {
        return value.size <= Profile_FILE_SIZE;
      }
      return true;
    }),
  dateOfBirth: Yup.date().required('Date of birth is required.'),
  currentCTC: Yup.number('Current CTC must be of type number.')
    .min(0, 'CTC should not be less then 0.')
    .max(99, 'CTC should not be more then 99.')
    .typeError('Only positive numbers are allowed.')
    .required('Current CTC is required.'),
  currentCompany: Yup.string('Current company must be of type string.')
    .trim()
    .required('Current Company is required.'),
  technologies: Yup.array()
    .required('Technology is required.')
    .min(1, 'Minimum 1 technology is required.'),
  currentAddress: Yup.string('Current Address must be of type string.')
    .trim()
    .max(500, 'Current Address not be more then 500 characters long.'),
  permanentAddress: Yup.string('Permanent Address must be of type string.')
    .trim()
    .max(500, 'Permanent Address not be more then 500 characters long.')
});

// Profile
export const changePasswordValidationSchema = Yup.object({
  oldPassword: Yup.string().trim().required('Old Password is required.'),
  password: Yup.string()
    .trim()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/^(?=.*[a-z]).*$/, 'Password must contain at least one lowercase letter.')
    .matches(/^(?=.*[A-Z]).*$/, 'Password must contain at least one uppercase letter.')
    .matches(/^(?=.*\d).*$/, 'Password must contain at least one digit.')
    .matches(
      /^(?=.*[!@#$%^&*()\-_=+{};:,<.>/?]).*$/,
      'Password must contain at least one special character.'
    )
    .matches(/^\S*$/, 'Password cannot contain spaces.'),
  confirmPassword: Yup.string()
    .trim()
    .required('Confirm Password is required.')
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
});

// Education
export const educationValidationSchema = Yup.object({
  details: Yup.string()
    .trim()
    .required('Education details are required.')
    .max(255, 'Education details can have maximum 255 characters.'),
  course: Yup.string()
    .trim()
    .required('Course is a required field.')
    .max(255, 'Course can have maximum 100 characters.'),
  university: Yup.string()
    .trim()
    .required('University is a required field.')
    .max(255, 'University can have maximum 100 characters.'),
  startDate: Yup.date()
    .nullable()
    .typeError('Start date is required.')
    .required('Start date is required.')
    .max(new Date(), 'Start date cannot be in the future.'),

  endDate: Yup.date()
    .nullable()
    .typeError('End date is required.')
    .required('End date is required.')
    .max(new Date(), 'End date cannot be in the future.')
    .when('startDate', (startDate, schema) => {
      return startDate
        ? schema.test('is-after-start-date', 'End date cannot be before start date.', (endDate) => {
            if (!startDate || !endDate) return true;
            const start = new Date(startDate);
            const end = new Date(endDate);
            const startYear = start.getFullYear();
            const startMonth = start.getMonth();
            const endYear = end.getFullYear();
            const endMonth = end.getMonth();
            return endYear > startYear || (endYear === startYear && endMonth >= startMonth);
          })
        : schema;
    }),
  grade: Yup.object({
    name: Yup.string().trim(),
    code: Yup.string().trim()
  })
    .required('Grade is a required field.')
    .test('not-empty', 'Grade is a required field.', (value) => {
      return value && Object.keys(value).length > 0;
    }),
  remarks: Yup.string().trim()
});

// Resume
export const resumeValidationSchema = Yup.object({
  resumeName: Yup.string()
    .trim()
    .required('Resume Name is required.')
    .max(255, 'Resume Name can have a maximum of 255 characters.'),
  file: Yup.mixed()
    .required('File is required.')
    .test('is-valid-file', 'File must be either an object or a valid HTTPS URL.', (value) => {
      if (!value) return false; // Field is required, so it cannot be null or undefined
      if (typeof value === 'object') return true; // Allow objects (e.g., uploaded files)
      if (typeof value === 'string' && value.startsWith('http')) return true; // Allow valid HTTPS URLs
      return false; // Invalid file
    })
    .test('file-size', 'File size must be less than 5MB.', (value) => {
      if (!value || typeof value !== 'object') return true;
      return value.size <= Resume_FILE_SIZE;
    })
});

// Project
export const projectValidationSchema = Yup.object({
  projectTitle: Yup.string()
    .trim()
    .required('Project Title are required.')
    .max(255, 'Project Title can have maximum 255 characters.'),
  technology: Yup.array()
    .required('Technology is a required field.')
    .min(1, 'Technology is a required field.'),
  duration: Yup.number()
    .required('Duration is a required field.')
    .min(0.01, 'Duration cannot less than 0.01 years.')
    .max(100, 'Duration cannot exceed 100.')
    .test('valid-format', 'Invalid format. Ensure months do not exceed 11.', (value) =>
      isValidExperienceFormat(value)
    ),
  projectDetails: Yup.string().trim().required('Project Details is a required field.')
});

// Employment
export const employmentValidationSchema = Yup.object({
  jobTitle: Yup.string()
    .trim()
    .required('Job Title is required.')
    .max(255, 'Job Title can have a maximum of 255 characters.'),
  currentEmployment: Yup.string().trim().required('Current Employment status is required.'),
  employmentType: Yup.string().trim().required('Employment Type is required.'),
  totalExperience: Yup.number()
    .required('Total Experience is required.')
    .min(0, 'Total Experience cannot less than 0 years.')
    .max(100, 'Total Experience cannot exceed 100 years.')
    .test('valid-format', 'Invalid format. Ensure months do not exceed 11.', (value) =>
      isValidExperienceFormat(value)
    ),
  companyName: Yup.string()
    .trim()
    .required('Company Name is required.')
    .max(255, 'Company Name can have a maximum of 255 characters.'),
  jobLocation: Yup.string()
    .trim()
    .required('Job Location is required.')
    .max(255, 'Job Location can have a maximum of 255 characters.'),
  startDate: Yup.date()
    .required('Start Date is required.')
    .max(new Date(), 'Start Date cannot be in the future.'),
  endDate: Yup.date()
    .required('End Date is required.')
    .min(Yup.ref('startDate'), 'End Date cannot be before Start Date.'),
  joiningSalary: Yup.number()
    .required('Joining Salary is required.')
    .max(100, 'Joining Salary should not more than 100 LPA.'),
  lastSalary: Yup.number()
    .required('Last Salary is required.')
    .max(100, 'Last Salary should not more than 100 LPA.')
});

//Jobs
export const ApplyJobValidationSchema = Yup.object().shape({
  firstName: Yup.string().trim().required('First Name is required'),
  lastName: Yup.string().trim().required('Last Name is required'),
  email: Yup.string().trim().email('Invalid email').required('Email is required'),
  mobile: Yup.string().trim().required('Mobile number is required'),
  totalExperience: Yup.number()
    .test('valid-format', 'Invalid format. Ensure months do not exceed 11.', (value) =>
      isValidExperienceFormat(value)
    )
    .nullable()
    .required('Total Experience is required.')
    .min(0, 'Total Experience cannot be less than 0 years.')
    .max(100, 'Total Experience cannot exceed 100 years.'),
  currentLocation: Yup.string().trim().required('Current location is required'),
  highestQualification: Yup.string().trim().required('Highest qualification is required'),
  currentCompany: Yup.string()
    .trim()
    .required('Current company is required')
    .max(255, 'Current company Name can have a maximum of 255 characters.'),
  currentCTC: Yup.string()
    .trim()
    .required('Current CTC is required')
    .min(1, 'Current CTC should not be less than 1 LPA.')
    .max(99, 'Current CTC should not more than 99 LPA.'),
  expectedCTC: Yup.string()
    .trim()
    .required('Expected CTC is required')
    .min(1, 'Expected CTC should not be less than 1 LPA.')
    .max(99, 'Expected CTC should not more than 99 LPA.'),
  noticePeriod: Yup.string().trim().required('Notice period is required'),
  resumeFile: Yup.string().trim().required('Resume is required')
});

// Contact US
export const contactUsValidationSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .matches(/^[a-zA-Z_ ]+$/, 'Name can consist of only alphanumeric characters.')
    .required('Name is a required field.')
    .min(3, 'Name should have minimum 3 characters.'),
  email: Yup.string()
    .trim()
    .email('This email is not a valid format.')
    .required('Email is a required field.'),
  mobile: Yup.string()
    .trim()
    .matches(/^[0-9]+$/, 'Mobile must be numeric value.')
    .length(10, 'Mobile should have 10 digits.')
    .required('Mobile is a required field.'),
  companyName: Yup.string().trim().required('Company Name is a required field.'),
  description: Yup.string()
    .trim()
    .required('Description is a required field.')
    .min(
      DESCRIPTION_CHAR_MIN_LIMIT,
      `Description should have minimum ${DESCRIPTION_CHAR_MIN_LIMIT} characters.`
    ),
  skills: Yup.array().required('Skills is a required field.')
});

//Fill Profile By Resume
export const ProfileByResumeValidationSchema = Yup.object({
  file: Yup.mixed()
    .required('File is required.')
    .test('is-valid-file', 'File must be either an object or a valid HTTPS URL.', (value) => {
      if (!value) return false;
      if (typeof value === 'object') return true;
      if (typeof value === 'string' && value.startsWith('http')) return true;
      return false;
    })
    .test('file-size', 'File size must be less than 5MB.', (value) => {
      if (!value || typeof value !== 'object') return true;
      return value.size <= Resume_FILE_SIZE;
    })
});

// Resume UserDetail Form
// Profile
export const ResumeUserDetailValidationSchema = Yup.object().shape({
  firstName: Yup.string()
    .trim()
    .nullable()
    .matches(/^[a-zA-Z_ ]+$/, 'Firstname can consist of only alphanumeric characters.')
    .min(3, 'Firstname should have minimum 3 characters.')
    .max(100, 'Firstname can have maximum 100 characters.'),
  middleName: Yup.string()
    .trim()
    .nullable()
    .matches(/^[a-zA-Z_ ]+$/, 'Middlename can consist of only alphanumeric characters.')
    .max(100, 'Middlename can have maximum 100 characters.'),
  lastName: Yup.string()
    .trim()
    .nullable()
    .matches(/^[a-zA-Z_ ]+$/, 'Lastname can consist of only alphanumeric characters.')
    .min(3, 'Lastname should have minimum 3 characters.')
    .max(100, 'Lastname can have maximum 100 characters.'),
  phone: Yup.string()
    .trim()
    .nullable()
    .matches(/^[0-9]+$/, 'Phone must be numeric value.')
    .length(10, 'Phone should have 10 digits.'),
  secondPhone: Yup.string()
    .trim()
    .matches(/^[0-9]+$/, 'Second Mobile must be numeric value.')
    .length(10, 'Second Mobile should have 10 digits.')
    .nullable(),
  currentLocation: Yup.string('Current location must be of type string.').nullable(),
  location: Yup.string('Current Location must be of type string.')
    .trim()
    .nullable()
    .when('currentLocation', {
      is: (value) => value === 'other',
      then: (schema) => schema.required('Current Location is required.')
    }),
  totalExperience: Yup.string()
    .trim()
    .test('valid-format', 'Invalid format. Ensure months do not exceed 11.', (value) =>
      isValidExperienceFormat(value)
    )
    .nullable(),
  gender: Yup.number().nullable(),
  maritalStatus: Yup.number().nullable(),
  dateOfBirth: Yup.date().nullable(),
  currentCompany: Yup.string('Current company must be of type string.').trim().nullable(),
  technologies: Yup.array().nullable(),
  currentAddress: Yup.string('Current Address must be of type string.')
    .trim()
    .nullable()
    .max(500, 'Current Address not be more then 500 characters long.'),
  permanentAddress: Yup.string('Permanent Address must be of type string.')
    .trim()
    .nullable()
    .max(500, 'Permanent Address not be more then 500 characters long.'),
  currentCTC: Yup.number('Current CTC must be of type number.')
    .min(0, 'CTC should not be less then 0.')
    .max(99, 'CTC should not be more then 99.')
    .typeError('Only positive numbers are allowed.')
    .nullable()
});

// Resume ProjectDetail Form
// Profile

export const ResumeProjectDetailValidationSchema = Yup.object().shape({
  projects: Yup.array().of(
    Yup.object().shape({
      projectTitle: Yup.string()
        .trim()
        .required('Project Title are required.')
        .max(255, 'Project Title can have maximum 255 characters.'),
      technology: Yup.array().nullable(),
      duration: Yup.number()
        .nullable()
        .min(0.01, 'Duration cannot less than 0.01 years.')
        .max(100, 'Duration cannot exceed 100.')
        .test('valid-format', 'Invalid format. Ensure months do not exceed 11.', (value) =>
          isValidExperienceFormat(value)
        ),
      projectDetails: Yup.string().trim().nullable()
    })
  )
});

// Resume EducationDetail Form
// Profile

export const ResumeEducationDetailValidationSchema = Yup.object().shape({
  educations: Yup.array().of(
    Yup.object().shape({
      details: Yup.string()
        .trim()
        .required('Education details are required.')
        .max(255, 'Education details can have maximum 255 characters.'),
      course: Yup.string().trim().nullable().max(255, 'Course can have maximum 100 characters.'),
      university: Yup.string()
        .trim()
        .nullable()
        .max(255, 'University can have maximum 100 characters.'),
      startDate: Yup.date()
        .nullable()
        .typeError('Start date is required.')
        .max(new Date(), 'Start date cannot be in the future.'),
      endDate: Yup.date()
        .nullable()
        .typeError('End date is required.')
        .max(new Date(), 'End date cannot be in the future.')
        .when('startDate', (startDate, schema) => {
          return startDate
            ? schema.test(
                'is-after-start-date',
                'End date cannot be before start date.',
                (endDate) => {
                  if (!startDate || !endDate) return true;
                  const start = new Date(startDate);
                  const end = new Date(endDate);
                  const startYear = start.getFullYear();
                  const startMonth = start.getMonth();
                  const endYear = end.getFullYear();
                  const endMonth = end.getMonth();
                  return endYear > startYear || (endYear === startYear && endMonth >= startMonth);
                }
              )
            : schema;
        }),
      grade: Yup.object({
        name: Yup.string().trim(),
        code: Yup.string().trim()
      }).nullable(),
      remarks: Yup.string().trim().nullable()
    })
  )
});

// Resume EmploymentDetail Form
// Profile

export const ResumeEmploymentDetailValidationSchema = Yup.object().shape({
  employments: Yup.array().of(
    Yup.object().shape({
      jobTitle: Yup.string()
        .trim()
        .required('Job Title is required.')
        .max(255, 'Job Title can have a maximum of 255 characters.'),
      currentEmployment: Yup.string().trim().nullable(),
      employmentType: Yup.string().trim().nullable(),
      totalExperience: Yup.number()
        .nullable()
        .min(0, 'Total Experience cannot less than 0 years.')
        .max(100, 'Total Experience cannot exceed 100 years.')
        .test('valid-format', 'Invalid format. Ensure months do not exceed 11.', (value) =>
          isValidExperienceFormat(value)
        ),
      companyName: Yup.string()
        .trim()
        .nullable()
        .max(255, 'Company Name can have a maximum of 255 characters.'),
      jobLocation: Yup.string()
        .trim()
        .nullable()
        .max(255, 'Job Location can have a maximum of 255 characters.'),
      startDate: Yup.date().nullable().max(new Date(), 'Start Date cannot be in the future.'),
      endDate: Yup.date()
        .nullable()
        .min(Yup.ref('startDate'), 'End Date cannot be before Start Date.'),
      joiningSalary: Yup.number()
        .nullable()
        .min(0, 'Joining Salary should not less than 0 LPA')
        .max(100, 'Joining Salary should not more than 100 LPA.'),
      lastSalary: Yup.number()
        .nullable()
        .min(0, 'Last Salary should not less than 0 LPA')
        .max(100, 'Last Salary should not more than 100 LPA.')
    })
  )
});
